import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, Search, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STATUSES = ["Open", "In Progress", "Waiting for User", "Resolved", "Closed"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const STATUS_STYLES = {
  Open: "bg-blue-100 text-blue-800", "In Progress": "bg-amber-100 text-amber-800",
  "Waiting for User": "bg-purple-100 text-purple-800", Resolved: "bg-green-100 text-green-800",
  Closed: "bg-slate-100 text-slate-600",
};

export default function AdminSupport() {
  const [tickets, setTickets] = useState(null);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (status) params.set("status", status);
    if (priority) params.set("priority", priority);
    api.get(`/admin/support/tickets?${params}`).then((r) => setTickets(r.data.tickets)).catch((e) => toast.error(formatApiError(e)));
  }, [q, status, priority]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openTicket = (t) => api.get(`/support/tickets/${t.id}`).then((r) => setActive(r.data.ticket)).catch((e) => toast.error(formatApiError(e)));

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    setSaving(true);
    try {
      await api.post(`/support/tickets/${active.id}/replies`, { body: reply.trim() });
      setReply("");
      toast.success("Response sent to the user");
      openTicket(active);
      load();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const changeStatus = async (newStatus) => {
    try {
      await api.patch(`/support/tickets/${active.id}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      openTicket(active);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-support-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Support Tickets</h1>
        <p className="mt-1 text-sm text-muted-foreground">Respond to Enterprise, Buyer and Seller support tickets.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input data-testid="support-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search ticket, enterprise, user" className="w-72 bg-white pl-8" />
        </div>
        <select data-testid="support-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select data-testid="support-priority-filter" value={priority} onChange={(e) => setPriority(e.target.value)}
          className="rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm">
          <option value="">All priorities</option>
          {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div className="overflow-x-auto rounded-lg border bg-white" data-testid="support-tickets-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Ticket</th>
                <th className="px-4 py-3 font-medium">Enterprise</th>
                <th className="px-4 py-3 font-medium">Raised By</th>
                <th className="px-4 py-3 font-medium">Priority</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Updated</th>
              </tr>
            </thead>
            <tbody>
              {!tickets && <tr><td colSpan={6} className="px-4 py-8 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" /></td></tr>}
              {tickets?.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground" data-testid="support-empty">No tickets found.</td></tr>}
              {tickets?.map((t) => (
                <tr key={t.id} data-testid={`support-row-${t.ticket_number}`} onClick={() => openTicket(t)}
                  className={`cursor-pointer border-b transition-colors last:border-0 hover:bg-slate-50 ${active?.id === t.id ? "bg-slate-100" : ""}`}>
                  <td className="px-4 py-3">
                    <p className="font-mono text-xs text-muted-foreground">{t.ticket_number}</p>
                    <p className="max-w-52 truncate font-medium">{t.title}</p>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{t.enterprise_name || "—"}</td>
                  <td className="px-4 py-3">
                    <p>{t.raised_by_name}</p>
                    <p className="text-xs text-muted-foreground">{t.raised_by_role.replace("_", " ")}</p>
                  </td>
                  <td className="px-4 py-3">{t.priority}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[t.status]}`}>{t.status}</span></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDateTime(t.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="overflow-hidden rounded-lg border bg-card" data-testid="admin-ticket-detail">
          {active ? (
            <>
              <div className="border-b px-5 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-mono text-xs text-muted-foreground">{active.ticket_number}</p>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{active.category}</span>
                  <span className="text-xs text-muted-foreground">{active.priority}</span>
                </div>
                <h2 className="mt-1 font-heading text-lg font-semibold" data-testid="admin-ticket-title">{active.title}</h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {active.enterprise_name} · {active.raised_by_name} ({active.raised_by_email}) · {active.raised_by_role.replace("_", " ")} · {fmtDateTime(active.created_at)}
                </p>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{active.description}</p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">Status:</span>
                  <select data-testid="admin-ticket-status-select" value={active.status} onChange={(e) => changeStatus(e.target.value)}
                    className="rounded-md border border-input bg-white px-2 py-1 text-xs shadow-sm">
                    {STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="max-h-[340px] space-y-3 overflow-y-auto p-5" data-testid="admin-ticket-replies">
                {active.replies.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No responses yet.</p>}
                {active.replies.map((r) => {
                  const isAdmin = r.by_role === "org_admin";
                  return (
                    <div key={r.id} className={`rounded-lg border p-3.5 ${isAdmin ? "border-blue-200 bg-blue-50/50" : "bg-white"}`}>
                      <p className="text-xs font-semibold">{r.by_name} <span className="font-normal text-muted-foreground">· {isAdmin ? "ProcDeal Support" : r.by_role.replace("_", " ")} · {fmtDateTime(r.created_at)}</span></p>
                      <p className="mt-1 whitespace-pre-wrap text-sm">{r.body}</p>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 border-t p-3">
                <input data-testid="admin-ticket-reply-input" value={reply} onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), sendReply())}
                  placeholder="Type response..." className="flex-1 rounded-md border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                <Button data-testid="admin-ticket-reply-send" disabled={saving || !reply.trim()} onClick={sendReply} className="bg-slate-900 hover:bg-slate-800">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Send Response
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <p className="text-sm text-muted-foreground">Select a ticket to view details and respond.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
